import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';
import { GridSchema } from '../../components/common/Table/grid.types';

export interface TableConfigFilter {
  entity: string;
}

export interface ColumnPreferences {
  [columnKey: string]: {
    visible: boolean;
    order?: number;
    width?: number;
  };
}

export interface UpdatePreferencesParams {
  entity: string;
  preferences: ColumnPreferences;
  view?: string;
}

export interface ViewInfo {
  view_name: string;
}

export interface CloneViewParams {
  entity: string;
  source_view?: string;
  target_view: string;
}

export interface ViewParams {
  entity: string;
  view?: string;
}

export const tableConfigApi = createApi({
  reducerPath: 'tableConfigApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['TableConfig'],
  keepUnusedDataFor: 300, // Keep table config cached for 5 minutes
  endpoints: (builder) => ({
    getTableSchema: builder.query<GridSchema, ViewParams>({
      query: ({ entity, view }) => ({
        url: `table-config/${entity}/schema/`,
        method: 'GET',
        params: view ? { view } : undefined,
      }),
      providesTags: (result, error, { entity, view }) => [
        { type: 'TableConfig', id: `${entity}-${view || 'default'}` },
        { type: 'TableConfig', id: entity },
        { type: 'TableConfig', id: 'LIST' },
      ],
    }),

    updateColumnPreferences: builder.mutation<void, UpdatePreferencesParams>({
      query: ({ entity, preferences, view }) => ({
        url: `table-config/${entity}/preferences/`,
        method: 'PATCH',
        body: preferences,
        params: view ? { view } : undefined,
      }),
      invalidatesTags: (result, error, { entity, view }) => [
        { type: 'TableConfig', id: `${entity}-${view || 'default'}` },
        { type: 'TableConfig', id: entity },
      ],
    }),

    getColumnPreferences: builder.query<ColumnPreferences, ViewParams>({
      query: ({ entity, view }) => ({
        url: `table-config/${entity}/preferences/`,
        method: 'GET',
        params: view ? { view } : undefined,
      }),
      providesTags: (result, error, { entity, view }) => [
        { type: 'TableConfig', id: `${entity}-${view || 'default'}-preferences` },
      ],
    }),

    resetColumnPreferences: builder.mutation<void, ViewParams>({
      query: ({ entity, view }) => ({
        url: `table-config/${entity}/preferences/`,
        method: 'DELETE',
        params: view ? { view } : undefined,
      }),
      invalidatesTags: (result, error, { entity, view }) => [
        { type: 'TableConfig', id: `${entity}-${view || 'default'}` },
        { type: 'TableConfig', id: `${entity}-${view || 'default'}-preferences` },
        { type: 'TableConfig', id: entity },
      ],
    }),

    // Named Views endpoints
    getViews: builder.query<ViewInfo[], string>({
      query: (entity) => ({
        url: `table-config/${entity}/views/`,
        method: 'GET',
      }),
      providesTags: (result, error, entity) => [
        { type: 'TableConfig', id: `${entity}-views` },
      ],
    }),

    cloneView: builder.mutation<void, CloneViewParams>({
      query: ({ entity, source_view, target_view }) => ({
        url: `table-config/${entity}/views/clone/`,
        method: 'POST',
        body: {
          source_view: source_view || 'default',
          target_view,
        },
      }),
      invalidatesTags: (result, error, { entity }) => [
        { type: 'TableConfig', id: `${entity}-views` },
        { type: 'TableConfig', id: entity },
      ],
    }),

    deleteView: builder.mutation<void, { entity: string; viewName: string }>({
      query: ({ entity, viewName }) => ({
        url: `table-config/${entity}/views/${encodeURIComponent(viewName)}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { entity }) => [
        { type: 'TableConfig', id: `${entity}-views` },
        { type: 'TableConfig', id: entity },
      ],
    }),
  }),
});

// Export hooks
export const {
  useGetTableSchemaQuery,
  useUpdateColumnPreferencesMutation,
  useGetColumnPreferencesQuery,
  useResetColumnPreferencesMutation,
  useGetViewsQuery,
  useCloneViewMutation,
  useDeleteViewMutation,
} = tableConfigApi;